import { LocalePluginProvider } from '@mote/l10n/providers/LocalePluginProvider'
import { VisuallyHidden } from '@mote/ui'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import {
  CloseButton,
  Durations,
  MotePlayerProviders,
  Scrubber
} from '../shared/components'
import { useMotePlayer } from '../shared/contexts/motePlayer.context'
import {
  CompactCard,
  Controls,
  EmojiPicker,
  MiniCard,
  Options,
  Title,
  Transcription
} from './components'
import {
  Container,
  ControlsContainer,
  Header,
  ScrubberContainer,
  ScrubberWrapper,
  Wrapper
} from './elements'

const MoteCardRoot = ({
  moteId,
  audioUrl,
  username,
  onDelete,
  onPlay,
  onPause,
  onEnd,
  onEmoji,
  activeEmojis,
  scrubber,
  transcription,
  transcriptionVisible,
  totalTime,
  scrubberPercentage,
  isPreparing,
  isLoading,
  dynamicWidth,
  isQuiescent,
  shouldEnableReactions,
  editor,
  isPrivateComments,
  isLandingPage,
  variant,
  isParentAside,
  onClose,
  style = {},
  isStandalone = true
}) => {
  const [currentTime, setCurrentTime] = useState(0)
  const [forcePause, setForcePause] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const optionsEnabled = React.useMemo(
    () => editor || onDelete,
    [editor, onDelete]
  )

  const deleteMote = React.useCallback(() => {
    if (!onDelete) return

    setShowEditor(false)
    onDelete()
  }, [onDelete])

  const options = React.useMemo(() => {
    return {
      init: true,
      src: audioUrl,
      initialPercentagePosition: scrubberPercentage,
      isStandalone,
      playbackCallbacks: {
        onPlay,
        onPause,
        onEnd,
        onDelete: deleteMote
      }
    }
  }, [
    audioUrl,
    scrubberPercentage,
    isStandalone,
    onPlay,
    onPause,
    onEnd,
    deleteMote
  ])

  const { playerProps } = useMotePlayer(options)

  React.useEffect(() => {
    const currTime = +totalTime * (+scrubberPercentage / 100)
    setCurrentTime(currTime)
    if (+scrubberPercentage) setForcePause(true)
  }, [scrubberPercentage, totalTime])

  const FullCard = (
    <Wrapper
      width={dynamicWidth ? '100%' : undefined}
      data-mote-id={moteId}
      contentEditable='false'
      suppressContentEditableWarning='true'
      style={style?.container}
      {...playerProps}
    >
      <VisuallyHidden as='h2'>{playerProps['aria-label']}</VisuallyHidden>
      {showEditor &&
        editor &&
        editor({
          onClose: () => setShowEditor(false)
        })}

      <Container
        height={transcriptionVisible && !isLoading ? 'auto' : undefined}
      >
        <Header>
          <Title
            isPrivateComments={isPrivateComments}
            username={username}
            isPreparing={isPreparing}
            isLoading={isLoading}
          />

          {onClose && <CloseButton onClick={onClose} />}

          {optionsEnabled && !isPreparing && !isLoading && (
            <Options
              moteId={moteId}
              onDelete={onDelete ? deleteMote : null}
              onEdit={editor ? () => setShowEditor(true) : null}
            />
          )}
        </Header>

        <ControlsContainer
          isTranscriptionVisible={transcriptionVisible && !isLoading}
        >
          <Controls disabled={isPreparing} loading={isLoading} />
        </ControlsContainer>

        <ScrubberContainer className='scrubber-container'>
          {scrubber && (
            <ScrubberWrapper>
              {!isPreparing && (
                <Durations currentTime={currentTime} totalTime={totalTime} />
              )}
              <Scrubber
                disabled={isPreparing}
                loading={isLoading}
                currentTime={currentTime}
                totalTime={totalTime}
              />
            </ScrubberWrapper>
          )}
        </ScrubberContainer>
        {transcriptionVisible && !isLoading && (
          <Transcription
            moteId={moteId}
            text={transcription}
            isQuiescent={isQuiescent}
            isLandingPage={isLandingPage}
            isLoading={isLoading}
          />
        )}
      </Container>
      {shouldEnableReactions && onEmoji && !isPreparing && !isLoading && (
        <EmojiPicker
          moteId={moteId}
          onEmoji={onEmoji}
          activeEmojis={activeEmojis}
          isNarrow={isParentAside}
          shouldEnableReactions={shouldEnableReactions}
        />
      )}
    </Wrapper>
  )

  function getCard() {
    switch (variant) {
      case 'lg':
        return FullCard
      case 'md':
        return (
          <CompactCard
            moteId={moteId}
            disabled={isPreparing}
            loading={isLoading}
            dynamicWidth={dynamicWidth}
            transcriptionVisible={transcriptionVisible}
            transcription={transcription}
            isQuiescent={isQuiescent}
            editor={editor}
            isStandalone={false}
          />
        )
      case 'sm':
        return (
          <MiniCard
            moteId={moteId}
            disabled={isPreparing}
            loading={isLoading}
            dynamicWidth={dynamicWidth}
            isStandalone={false}
          />
        )
      default:
        return FullCard
    }
  }

  return window.chrome?.extension ? (
    <LocalePluginProvider>{getCard()}</LocalePluginProvider>
  ) : (
    getCard()
  )
}

export const MoteCard = ({ autoFocus, ...rest }) => (
  <MotePlayerProviders autoFocus={autoFocus} isStandalone={rest.isStandalone}>
    <MoteCardRoot {...rest} />
  </MotePlayerProviders>
)

MoteCard.propTypes = {
  moteId: PropTypes.string.isRequired,
  audioUrl: PropTypes.string.isRequired,
  username: PropTypes.string,
  onDelete: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnd: PropTypes.func,
  onEmoji: PropTypes.func,
  activeEmojis: PropTypes.array,
  shouldEnableReactions: PropTypes.bool,
  scrubber: PropTypes.bool,
  transcription: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  transcriptionVisible: PropTypes.bool,
  isPreparing: PropTypes.bool,
  dynamicWidth: PropTypes.bool,
  isQuiescent: PropTypes.bool,
  dominantLanguage: PropTypes.string,
  editor: PropTypes.func,
  isPrivateComments: PropTypes.bool,
  isLandingPage: PropTypes.bool,
  variant: PropTypes.string,
  isParentAside: PropTypes.bool,
  // Used for screenshot generation
  totalTime: PropTypes.string,
  scrubberPercentage: PropTypes.string,
  style: PropTypes.object
}

MoteCard.defaultProps = {
  activeEmojis: [],
  scrubber: true,
  isPreparing: false,
  dynamicWidth: false,
  shouldEnableReactions: false,
  transcriptionVisible: false,
  isPrivateComments: false,
  isLandingPage: false,
  variant: 'lg',
  isParentAside: false
}
